import React, { ChangeEvent, Component, FormEvent } from 'react';
import { Dialog } from 'primereact/dialog';
import Icon from '../../icons/Icon';
import { CustomFields, type SuspiciousActivityReport } from '../../domain/case/SuspiciousActivityReport';
import SarXmlConverter from '../../util/SarXmlConverter';
import moment from 'moment';
import SarXmlValidation from '../../util/SarXmlValidation';
import GercekKisilerAccordion from './components/GercekKisilerAccordion';
import DigerKisilerAccordion from './components/DigerKisilerAccordion';
import SupheliIslemlerAccordion from './components/SupheliIslemlerAccordion';
import HesaplarAccordion from './components/HesaplarAccordion';
import SupheKategorileriAccordion from './components/SupheKategorileriAccordion';
import BildirimAciklamaSection from './components/BildirimAciklamaSection';
import SarActionFooter from './components/SarActionFooter';
import TemelBilgilerSection from './components/TemelBilgilerSection';
import type { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import type { MonacoEditorInstance, MonacoNamespace, SarCategoryKey, SarValidationError, SarValidationResult } from './SarInterfaces';
import YukumluSection from './components/YukumluSection';
import LanguageUtil from '../../util/LanguageUtil';
import SarCategoryList from './components/SarCategoryList';
import type { TransactionData } from '../../domain/stream/transaction/TransactionDomains';

class SuspiciousActivityReportSidebarComponent extends Component<Props, State> {
  public state: State = new State();

  public render() {
    const { validateVisible, validate } = this.state;
    return (
      <div>
        {this.renderSidebar()}
        <Dialog header={this.getValidateHeader} className={'w-2/5 h-2/4'} visible={validateVisible} onHide={this.validateCancel}>
          <div>
            {validate?.errors.map((error, index) => (
              <div key={index}>
                • <span className={'text-f-black-2 font-normal text-md'}>{error.message}</span>
              </div>
            ))}
          </div>
        </Dialog>
      </div>
    );
  }

  private renderSidebar = () => {
    const { visible } = this.props;
    const { selectedCategory, validate, xmlContent } = this.state;
    return (
      <div
        className={`fixed inset-0 z-[999] transition-opacity duration-500 ease-in-out ${
          visible ? 'opacity-100 pointer-events-auto bg-[#101828]/40' : 'opacity-0 pointer-events-none'
        }`}
        onClick={this.onCancel}
      >
        <aside
          className={`absolute top-0 right-0 h-full w-full 2xl:w-[1000px] max-w-[880px] 2xl:max-w-[1000px] bg-white flex flex-col shadow-xl transition-transform duration-1000 ease-in-out ${
            visible ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          <header className={'flex items-center justify-between border-b bg-[#F9FAFB] px-5 2xl:px-6 h-[52px] 2xl:h-[56px] w-full'}>
            <div className={'flex items-center gap-x-3'}>
              <Icon icon={'sar-icon'} size={18} />
              <div className={'flex flex-col'}>
                <span className={'text-[#1D2939] text-base font-semibold'}>
                  {LanguageUtil.get({
                    id: `common.label.suspiciousActivityReport`,
                    defaultMessage: 'Suspicious Activity Report',
                  })}
                </span>
                <span className={'text-[#667085] text-xs'}>MASAK v2</span>
              </div>
            </div>
            <div className={'w-5 h-5 flex items-center justify-center hover:bg-f-gray-2 transition-all duration-200 rounded-sm '}>
              <Icon icon={'multiplier'} className={'text-[#475467] hover:cursor-pointer'} onClick={this.onCancel} />
            </div>
          </header>
          <div className={'flex flex-1 overflow-hidden'}>
            <SarCategoryList selectedCategory={selectedCategory} onSelectCategory={this.onSelectCategory} validate={validate} />
            <div className={'flex-1 flex flex-col pt-3 h-full'}>
              <div className={'flex-1 overflow-y-auto'}>{this.renderCategoryContent()}</div>
            </div>
          </div>
          <SarActionFooter onValidate={this.onValidate} onCancel={this.onCancel} onApply={this.onApply} validate={validate} xmlContent={xmlContent} />
        </aside>
      </div>
    );
  };

  private renderCategoryContent = () => {
    const { selectedCategory } = this.state;

    switch (selectedCategory) {
      case 'yukumlu':
        return this.renderYukumluContent();
      case 'temelbilgiler':
        return this.renderTemelBilgilerContent();
      case 'bildirimaciklama':
        return this.renderBildirimAciklamaSectionContent();
      case 'gercekkisiler':
        return this.renderGercekKisilerContent();
      case 'digerkisiler':
        return this.renderDigerKisilerContent();
      case 'supheliislemler':
        return this.renderSupheliIslemlerContent();
      case 'hesaplar':
        return this.renderHesaplarContent();
      case 'suphekategorileri':
        return this.renderSupheKategorileriContent();
      default:
        return <div className={'pl-4 pb-6 pt-3 2xl:pt-4'} />;
    }
  };

  private validateCancel = () => {
    this.setState({ validateVisible: false });
  };

  private onSelectCategory = (category: SarCategoryKey) => {
    this.setState({ selectedCategory: category });
  };

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.data !== this.props.data) {
      this.setState({ xmlContent: this.dataToCustomXml() });
    }
  }

  private getValidateHeader = () => {
    return (
      <div className={'flex items-center justify-between align-center'}>
        <div className={'flex items-center justify-start align-center w-full lg:w-auto gap-x-2'}>
          <Icon icon={'sar-icon'} size={16} />
          <div className={'text-f-black-2 font-normal text-md'}>Error Messages</div>
        </div>
      </div>
    );
  };

  private renderYukumluContent = () => {
    const { xmlContent, customFields, validate } = this.state;

    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <YukumluSection
          xmlContent={xmlContent}
          fallbackValue={customFields.yukumlu}
          onChangeYukumlu={(value) => {
            const { customFields } = this.state;
            customFields.yukumlu = value;
            this.setState({ customFields });
            this.updateXmlField(value, '<yukumlu>', '</yukumlu>');
          }}
          onEditorWillMount={this.handleEditorWillMount}
          onEditorDidMount={this.handleEditorDidMount}
          onEditorChange={this.onEditorChange}
          validate={validate}
        />
      </div>
    );
  };

  private renderTemelBilgilerContent = () => {
    const { customFields, xmlContent, validate } = this.state;

    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <TemelBilgilerSection
          xmlContent={xmlContent}
          bildirimKategorisi={customFields.bildirimKategorisi}
          aciliyet={customFields.aciliyet}
          terorOrgutu={customFields.terorOrgutu}
          yerelReferansNoFallback={customFields.yerelReferansNo}
          oncekiBildirimReferansNoFallback={customFields.oncekiBildirimReferansNo}
          onSelectBildirimKategorisi={this.onSelectBildirimKategorisi}
          onChangeYerelReferansNo={this.onChangeYerelReferansNo}
          onChangeOncekiBildirimReferansNo={this.onChangeOncekiBildirimReferansNo}
          onSelectAciliyet={this.onSelectAciliyet}
          onSelectTerorOrgutu={this.onSelectTerorOrgutu}
          validate={validate}
        />
      </div>
    );
  };

  private renderGercekKisilerContent = () => {
    const { xmlContent, validate } = this.state;
    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <GercekKisilerAccordion xmlContent={xmlContent} mutateXmlContent={this.mutateXmlContent} validate={validate} />
      </div>
    );
  };

  private renderBildirimAciklamaSectionContent = () => {
    const { xmlContent, customFields, validate } = this.state;
    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <BildirimAciklamaSection
          xmlContent={xmlContent}
          fallbackValue={customFields.bildirimAciklama}
          onChange={this.onChangeBildirimAciklama}
          validate={validate}
        />
      </div>
    );
  };

  private renderDigerKisilerContent = () => {
    const { xmlContent, validate } = this.state;
    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <DigerKisilerAccordion xmlContent={xmlContent} mutateXmlContent={this.mutateXmlContent} validate={validate} />
      </div>
    );
  };

  private renderSupheliIslemlerContent = () => {
    const { xmlContent, validate } = this.state;
    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <SupheliIslemlerAccordion xmlContent={xmlContent} mutateXmlContent={this.mutateXmlContent} validate={validate} />
      </div>
    );
  };

  private renderHesaplarContent = () => {
    const { xmlContent, validate } = this.state;
    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <HesaplarAccordion xmlContent={xmlContent} mutateXmlContent={this.mutateXmlContent} validate={validate} />
      </div>
    );
  };

  private renderSupheKategorileriContent = () => {
    const { xmlContent, validate } = this.state;
    return (
      <div className='h-full w-full pr-6 pb-6 flex flex-col '>
        <SupheKategorileriAccordion xmlContent={xmlContent} mutateXmlContent={this.mutateXmlContent} validate={validate} />
      </div>
    );
  };

  private onSelectBildirimKategorisi = (e: FormicaSelectBoxItem) => {
    const { customFields } = this.state;
    customFields.bildirimKategorisi = e.value;
    this.setState({ customFields });
    this.updateXmlField(e.value, '<bildirimkategorisi>', '</bildirimkategorisi>');
  };

  private onChangeYerelReferansNo = (e: FormEvent<HTMLInputElement>) => {
    const { customFields } = this.state;
    customFields.yerelReferansNo = e.currentTarget.value;
    this.setState({ customFields });
    this.updateXmlField(e.currentTarget.value, '<yerelreferansno>', '</yerelreferansno>');
  };

  private onChangeOncekiBildirimReferansNo = (e: FormEvent<HTMLInputElement>) => {
    const { customFields } = this.state;
    customFields.oncekiBildirimReferansNo = e.currentTarget.value;
    this.setState({ customFields });
    this.updateXmlField(e.currentTarget.value, '<oncekibildirimreferansno>', '</oncekibildirimreferansno>');
  };

  private onSelectAciliyet = (e: FormicaSelectBoxItem) => {
    const { customFields } = this.state;
    customFields.aciliyet = e.value;
    this.setState({ customFields });
    this.updateXmlField(e.value, '<aciliyet>', '</aciliyet>');
  };

  private onSelectTerorOrgutu = (e: FormicaSelectBoxItem) => {
    const { customFields } = this.state;
    customFields.terorOrgutu = e.value;
    this.setState({ customFields });
    this.updateXmlField(e.value, '<terororgutu>', '</terororgutu>');
  };

  private onChangeBildirimAciklama = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { customFields } = this.state;
    customFields.bildirimAciklama = e.currentTarget.value;
    this.setState({ customFields });
    this.updateXmlField(e.currentTarget.value, '<bildirimaciklama>', '</bildirimaciklama>');
  };

  private updateXmlField(value: string, tagStart: string, tagEnd: string) {
    const { xmlContent, validate } = this.state;
    if (!xmlContent) return;

    const startIdx = xmlContent.indexOf(tagStart);
    const endIdx = xmlContent.indexOf(tagEnd);
    if (startIdx === -1 || endIdx === -1) {
      return;
    }
    const insertPosition = startIdx + tagStart.length;
    const updatedXmlContent = xmlContent.substring(0, insertPosition) + value + xmlContent.substring(endIdx);

    this.setState({ xmlContent: updatedXmlContent }, () => {
      if (validate !== undefined) {
        const sarValidation: SarXmlValidation = new SarXmlValidation(updatedXmlContent);
        const newValidate: SarValidationResult = sarValidation.validate();
        this.setState({ validate: newValidate });
        this.showErrorsInEditor(newValidate.errors);
      }
    });
  }

  private dataToCustomXml() {
    const { customFields } = this.state;
    const { data, sarConfiguration } = this.props;
    if (sarConfiguration == undefined || data == undefined || data.length == 0) return;
    const sar: SarXmlConverter = new SarXmlConverter(data, customFields, sarConfiguration!);
    return this.formatXML(sar.parse());
  }

  private formatXML = (xml: string): string => {
    const slice = '  ';
    const reg = />\n\s*</g;
    let formatted = '';
    let pad = 0;

    const sanitizedXml = xml.trim().replace(reg, '>\n<');
    sanitizedXml.split('\n').forEach((node: string) => {
      let indent = 0;
      if (/.+<\/\w[^>]*>$/.test(node)) {
        indent = 0;
      } else if (/^<\/\w/.test(node)) {
        if (pad !== 0) {
          pad -= 1;
        }
      } else if (/^<\w([^>]*[^/])?>.*$/.test(node)) {
        indent = 1;
      } else {
        indent = 0;
      }
      const padding = new Array(pad + 1).join(slice);
      formatted += padding + node + '\n';
      pad += indent;
    });

    return formatted;
  };

  private onEditorChange = (newValue: string | undefined) => {
    if (newValue) {
      this.setState({ xmlContent: newValue });
    }
  };

  private onApply = () => {
    const { xmlContent } = this.state;
    if (xmlContent == undefined) return;
    this.downloadXML(xmlContent, `sib_${moment().format('YYYY_MM_DD_HH_mm_ss').valueOf()}.xml`);
  };

  private onCancel = () => {
    this.props.onClose();
    this.setState({
      validate: undefined,
      customFields: {
        yukumlu: '',
        bildirimKategorisi: '',
        yerelReferansNo: '',
        oncekiBildirimReferansNo: '',
        aciliyet: '',
        terorOrgutu: '',
        bildirimAciklama: '',
      },
      selectedCategory: 'yukumlu',
    });
  };

  private onValidate = () => {
    const { xmlContent } = this.state;
    if (xmlContent == undefined) return;
    const sarValidation: SarXmlValidation = new SarXmlValidation(xmlContent);
    const validate: SarValidationResult = sarValidation.validate();

    this.setState({ validate: validate });
    this.showErrorsInEditor(validate.errors);
  };

  private downloadXML = (content: string, filename: string) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'application/xml' });
    element.href = URL.createObjectURL(file);
    element.download = filename;

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  private showErrorsInEditor = (errors: SarValidationError[]) => {
    const { editor, monacoInstance } = this.state;
    if (editor == null || monacoInstance == null) {
      return;
    }
    const model = editor.getModel();
    if (model == null) {
      return;
    }
    const markers = errors.map((error: SarValidationError) => {
      return {
        startLineNumber: error.lineStart,
        startColumn: error.colStart,
        endLineNumber: error.lineEnd,
        endColumn: error.colEnd,
        message: error.message,
        severity: monacoInstance.MarkerSeverity.Error,
      };
    });
    monacoInstance.editor.setModelMarkers(model, 'owner', markers);
  };

  private handleEditorWillMount = (monaco: MonacoNamespace) => {
    monaco.editor.defineTheme('my-theme', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#F9FAFB',
      },
    });
    this.setState({ monacoInstance: monaco });
  };

  private handleEditorDidMount = (editor: MonacoEditorInstance) => {
    this.setState({ editor });
  };

  private mutateXmlContent = (mutator: (document: Document) => void): void => {
    const { xmlContent, validate } = this.state;
    if (xmlContent == undefined || xmlContent.trim().length === 0) {
      return;
    }
    const parser = new DOMParser();
    const document = parser.parseFromString(xmlContent, 'text/xml');
    mutator(document);
    const serializer = new XMLSerializer();
    const serialized = serializer.serializeToString(document);
    const updatedXmlContent = this.formatXML(serialized);

    this.setState({ xmlContent: updatedXmlContent }, () => {
      if (validate !== undefined) {
        const sarValidation: SarXmlValidation = new SarXmlValidation(updatedXmlContent);
        const newValidate: SarValidationResult = sarValidation.validate();
        this.setState({ validate: newValidate });
        this.showErrorsInEditor(newValidate.errors);
      }
    });
  };
}

class State {
  xmlContent: string | undefined;
  customFields: CustomFields = {
    yukumlu: '',
    bildirimKategorisi: '',
    yerelReferansNo: '',
    oncekiBildirimReferansNo: '',
    aciliyet: '',
    terorOrgutu: '',
    bildirimAciklama: '',
  };
  validate: SarValidationResult | undefined;
  validateVisible: boolean = false;
  monacoInstance: MonacoNamespace | null = null;
  editor: MonacoEditorInstance | null = null;
  selectedCategory: SarCategoryKey = 'yukumlu';
}

export type { SarValidationError, SarValidationResult } from './SarInterfaces';

export interface Props {
  visible: boolean;
  data: TransactionData[];
  onClose: () => void;
  sarConfiguration: SuspiciousActivityReport | undefined;
}

export default SuspiciousActivityReportSidebarComponent;
